// Package imports:
import React, { useContext, useEffect, useMemo, useState } from 'react';
// Component imports:
import DisplayBox from '../DisplayBox/DisplayBox';
import FilterItem from '../../ui-elements/Filter/FilterItem/FilterItem';
import Loading from '../../ui-elements/Loading/Loading';
import Button from '../../ui-elements/Button/Button';
import Table from '../../ui-elements/Table/Table';
import ErrorAlert from '../ErrorAlert/ErrorAlert';
// Service imports:
import { GET_KELDAN_API_URL } from '../../services/config';
import { useApiLmdData } from '../../services/apiHooks';
import { getSearchTermsFromSearch } from '../../services/utils';
import { ErrorMessages } from '../../services/errorMessages';
// Type imports:
import { Fetched } from '../../types/Types';
import { IApiLmdAutocomplete, IApiLmdAutocompleteItem, ICompanySearchResults } from '../../types/SearchTypes';
// Context imports:
import { AccessTokenContext } from '../../contexts/AccessTokenContext';


interface IProps {
    term?: string | null
}

type SearchFilterType = 'Fyrirtæki' | 'Hlutabréf' | 'Skuldabréf' | 'Gjaldmiðlar' | 'Sjóðir' | 'Vísitölur' | 'Skuldabréfa Vísitölur'; 
// type SearchFilterType = 'Efnisgreinar' | 'Fyrirtæki' | 'Hlutabréf' | 'Skuldabréf' | 'Gjaldmiðlar' | 'Sjóðir' | 'Vísitölur' | 'Skuldabréfa Vísitölur'; 

type SettingsBooleanMap = {
    [key in SearchFilterType]: boolean
}

const EMPTY_SETTINGS: SettingsBooleanMap = {
    'Fyrirtæki': true,
    'Hlutabréf': true,
    'Skuldabréf': true,
    'Gjaldmiðlar': true,
    'Sjóðir': true,
    'Vísitölur': true,
    'Skuldabréfa Vísitölur': true,
}

const SearchPage: React.FC<IProps> = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const term = urlParams.get('search');
    const accessToken = useContext(AccessTokenContext);
    // Autocomplete request variables:
    const [autocompleteData] = useApiLmdData<IApiLmdAutocomplete>(`/v1/static_data/v1/autocomplete/keldan/`, accessToken);
    // Company search
    const [companyList, setCompanyList] = useState<Fetched<IApiLmdAutocompleteItem[]>>(null);
    // Settings: filter by type.
    const [ settings, setSettings ] = useState(EMPTY_SETTINGS);

    // Memos:
    const searchTerms = useMemo(() => getSearchTermsFromSearch(term), [term]);
    // One data object to track any change in any request.
    const data: {
        [T in SearchFilterType]: Fetched<IApiLmdAutocompleteItem[]>
    } = useMemo(() => {
        const filterFetchedData = (data: Fetched<IApiLmdAutocompleteItem[]>) => {
            if (data === null || data instanceof Error) return data;
            return data.filter(item => {
                // Return true if:
                // - symbol contains all search terms.
                // OR name contains all search terms.
                let symbolContainsAllSearchTerms = true;
                let nameContainsAllSearchTerms = true;
                for (let searchTerm of searchTerms) {
                    if (item.Symbol === null || !item.Symbol.toLowerCase().includes(searchTerm.toLowerCase()))
                        symbolContainsAllSearchTerms = false;
                    if (item.Name === null || !item.Name.toLowerCase().includes(searchTerm.toLowerCase()))
                        nameContainsAllSearchTerms = false;
                }
                return (symbolContainsAllSearchTerms || nameContainsAllSearchTerms);
            });
        }
        return {
            'Hlutabréf': filterFetchedData(autocompleteData.data?.shares ?? autocompleteData.error),
            'Skuldabréf': filterFetchedData(autocompleteData.data?.bonds ?? autocompleteData.error),
            'Gjaldmiðlar': filterFetchedData(autocompleteData.data?.currencies ?? autocompleteData.error),
            'Sjóðir': filterFetchedData(autocompleteData.data?.funds ?? autocompleteData.error),
            'Vísitölur': filterFetchedData(autocompleteData.data?.indexes ?? autocompleteData.error),
            'Skuldabréfa Vísitölur': filterFetchedData(autocompleteData.data?.bond_indexes ?? autocompleteData.error),
            'Fyrirtæki': companyList
        }
    }, [ autocompleteData, companyList ])

    // Reset settings on data change
    useEffect(() => {
        setSettings(EMPTY_SETTINGS)
    }, [data]);

    // Companies, unlike other autocomplete items, must be explicitly searched for.
    // This effect takes care of that.
    useEffect(() => {
        const companySearch = async () => {
            if (term === null) return;
            if (term.length < 1) return;
            try {
                const url = `${GET_KELDAN_API_URL()}/Company/SearchRskConnector?searchTerm=${term}`;
                const response = await fetch(url);
                if (response.ok) {
                    const body: ICompanySearchResults = await response.json();
                    const companies: IApiLmdAutocompleteItem[] = body.results?.map(([Symbol, Name]) => ({
                        Symbol,
                        Name,
                        Tickertype: 'company'
                    })) ?? [];
                    setCompanyList(companies);
                } else {
                    setCompanyList(new Error(ErrorMessages.RequestFailed));
                }
            } catch (e) {
                setCompanyList(new Error(ErrorMessages.NetworkError));
            }
        }
        companySearch();
    }, []);

    const displaySetting = (title: SearchFilterType, value: boolean, index: number) => {
        const dataList = data[title];
        // Don't render if loading or empty
        if (dataList === null || (Array.isArray(dataList) && dataList.length === 0)) return null;
        // If data has error, show it disabled.
        if (dataList instanceof Error) return (
            <li key={index}>
                <FilterItem
                    size='lg'
                    showCheck={true}
                    text={title}
                    selected={true}
                    toggleSelected={() => {}}
                    disabled
                />
            </li>
        )
        return (
            <li key={index}>
                <FilterItem
                    size='lg'
                    showCheck={true}
                    text={title}
                    selected={value}
                    toggleSelected={() => {
                        setSettings({
                            ...settings,
                            [title]: !value
                        })
                    }}
                />
            </li>
        )
    }

    const getItemHref = (item: IApiLmdAutocompleteItem) => {
        switch (item.Tickertype) {
            case 'share':
                return `/Markadir/Hlutabref/${item.Symbol}`;
            case 'bond':
                return `/Markadir/Skuldabref/${item.Symbol}`;
            case 'currency':
                return `/Markadir/Gjaldmidlar/${item.Symbol}`;
            case 'fund':
                return `/Markadir/Sjodir/${item.Symbol}`;
            case 'index':
                return `/Markadir/Visitolur/${item.Symbol}`;
            case 'bondIndex':
                return `/Markadir/Visitolur/${item.Symbol}`;
            case 'company':
                return `/Fyrirtaeki/Yfirlit/${item.Symbol}`;
            default:
                break;
        }
    }

    const displaySectionTable = (data: Fetched<IApiLmdAutocompleteItem[]>, title: SearchFilterType, index: number) => {
        if (
            data === null
            || !settings[title]
        ) return null;

        if (data instanceof Error) {
            return (
                <div className='table-section' key={index}>
                    <h4>{title}</h4>
                    <ErrorAlert error={data} />
                </div>
            )
        }

        if (data.length === 0) return null;

        const sortedData = [...data].sort((a, b) => {
            if (a.Name === null && b.Name === null) return 0;
            if (a.Name === null) return -1;
            if (b.Name === null) return 1;
            if (a.Name < b.Name) return -1
            if (a.Name > b.Name) return 1;
            else return 0;
        });

        return (
            <div className='table-section' key={index}>
                <h4>{title}</h4>
                <Table 
                    data={sortedData}
                    columns={[{
                        title: 'Nafn',
                        dataAccessor: ( datum ) => ( 
                            <a className='clickableRow' href={getItemHref(datum)}>
                                <span className='name'>{datum.Name + ' ' + datum.Symbol}</span>
                            </a>
                        ),
                        textAlign: 'left',
                    },{
                        title: '',
                        dataAccessor: ( datum ) => ( 
                            <a className='anchorWrapForButton' href={getItemHref(datum)}>
                                <span>
                                    <Button buttonType='secondary' size='sm'>Skoða</Button>
                                </span>
                            </a>
                        )
                    }]}
                    tableSize='lg'
                />
            </div>
        )
    }

    const isLoading = useMemo(() => {
        return Object.values(data).some(fetched => (fetched === null));
    }, [ autocompleteData, data ])

    const isEmpty = useMemo(() => {
        // If any dataList has values or is null, return false immediately.
        for (const dataList of Object.values(data)) {
            if (dataList === null) return false;
            if (Array.isArray(dataList) && dataList.length > 0) return false;
        }
        // If only errors and empty arrays => it is empty.
        return true
    }, [ data ]);

    return (
        <div className='main KCL_searchPage'>
            <div className="main__inner-fluid">
                <div className="shell">
                    <div className="section">
                        <h2>Leitarniðurstöður fyrir {term}</h2>
                        <DisplayBox>
                            {
                                (isLoading)
                                ? <Loading />
                                : (isEmpty)
                                ? <h4>Engar leitarniðurstöður fundust fyrir "{term}"</h4>
                                : (
                                <>
                                    <strong className='filter-text'>Sía eftir tegund efnis:</strong>
                                    <div className='KCL_filter'>
                                    <ul className='filter_container'>
                                        {Object.entries(settings).map(([title, value], index) => displaySetting(title as SearchFilterType, value, index))}
                                    </ul>
                                        <div>
                                            {Object.entries(data).map(([title, dataList], index) => displaySectionTable(dataList, title as SearchFilterType, index))}
                                        </div>
                                    </div>
                                </>
                                )
                            }
                        </DisplayBox>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default SearchPage;